import axios from "axios";
import { logEvent, logException } from "../helper/AppInsightsHelper";
import { IAzSearchInput } from "../interfaces/Customer.interfaces";
import { ENDPOINTS } from "./ServiceConfig";
import { store } from "../store/store";
import { selectUserToken } from "../slices/Auth.slice";

const controller = new AbortController();

axios.interceptors.request.use(
  (config: any) => {
    // Get token from Redux store using selector (as in slice files)
    const state = store.getState();
    const token = selectUserToken(state);
    config.headers["Authorization"] = `Bearer ${token || ""}`;
    config.headers["Content-Type"] = "application/json";
    config.headers["Access-Control-Allow-Origin"] = "*";
    config.headers["Cache-Control"] = "no-cache";
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return error;
  }
);

const handleApiError = (error: any, serviceName: string, methodName: string) => {
  // Check if it's an axios error with response
  if (error.response) {
    // Server responded with error status (4xx, 5xx)
    const status = error.response.status;
    const statusText = error.response.statusText;
    const errorData = error.response.data;

    logException("AzSearchServices", serviceName, methodName, {
      status,
      statusText,
      url: error.config?.url,
      method: error.config?.method,
      requestData: error.config?.data,
      responseData: errorData,
      headers: error.response.headers
    });
  } else if (error.request) {
    // Request was made but no response received (network error, timeout, etc.)
    logException("AzSearchServices", serviceName, methodName, {
      url: error.config?.url,
      method: error.config?.method,
      timeout: error.code === "ECONNABORTED",
      requestData: error.config?.data
    });
  } else {
    // Something else happened (request setup error, etc.)
    logException("AzSearchServices", serviceName, methodName, {
      errorType: "setup",
      originalError: error
    });
  }
};

const makeApiCall = async (endpoint: string, data: IAzSearchInput, serviceName: string, methodName: string) => {
  return axios
    .post(endpoint, data, { signal: controller.signal })
    .then((response) => {
      // Check if the HTTP response status code indicates success
      // HTTP status codes 200-299 are considered successful responses
      // 200: OK, 201: Created, 202: Accepted, 204: No Content, etc.
      if (response.status >= 200 && response.status < 300) {
        logEvent(`${methodName} API call success`, "", serviceName);
        return response?.data;
      } else {
        handleApiError(response, serviceName, methodName);
        return {
          count: 0,
          values: [],
          size: data.size,
          skip: data.skip
        };
      }
    })
    .catch((error) => {
      handleApiError(error, serviceName, methodName);
      return error;
    });
};

export const getCustomers = async (data: IAzSearchInput) => {
  return makeApiCall(`${ENDPOINTS.BASE_URL}customer/getCustomers`, data, "AzSearch service", "getCustomers");
};

export const getPartners = async (data: IAzSearchInput) => {
  return makeApiCall(`${ENDPOINTS.BASE_URL}Partner/getPartners`, data, "AzSearch service", "getPartners");
};

export const getProducts = async (data: IAzSearchInput) => {
  return makeApiCall(`${ENDPOINTS.BASE_URL}product/getProducts`, data, "AzSearch service", "getProducts");
};

export const getReports = async (data: IAzSearchInput) => {
  return makeApiCall(`${ENDPOINTS.BASE_URL}reportcatalog/getReports`, data, "AzSearch service", "getReports");
};

export const getFeatureRequests = async (data: IAzSearchInput) => {
  return makeApiCall(`${ENDPOINTS.BASE_URL}featureRequest/getFeatureRequests`, data, "AzSearch service", "getFeatureRequests");
};
